import { HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { map as lodashMap } from 'lodash'
import { Observable } from 'rxjs'
import { map as oMap } from 'rxjs/operators'

import { EstimateCategoryModel, EstimateCategoryStatus, EstimateTemplateModel } from '../model/estimate'
import { AuthenticatedRestService } from './auth-rest.service'
import { PagedRestResponse, RestResponse } from './rest'
import { ParamsObject } from './rest/rest.service'

export interface EstimateTemplatesCollectionQuery extends ParamsObject {
  name?: string
  facilityIds?: string[]
  categoryId?: string
  all?: boolean
  pageIndex?: number
  pageSize?: number
}

export interface EstimateCategoriesCollectionQuery extends ParamsObject {
  parentId?: string
  name?: string
  pageIndex?: number
  pageSize?: number
  startAt?: string
}

@Injectable({
  providedIn: 'root',
})
export class EstimateTemplateApiService extends AuthenticatedRestService {
  getEstimateTemplatesCollection(
    query: EstimateTemplatesCollectionQuery,
    forceRefresh = false,
  ): Observable<EstimateTemplateModel[]> {
    return this.getEstimateTemplatesCollectionResponse(query, forceRefresh).pipe(
      oMap((response) => response.data),
    )
  }

  getEstimateTemplatesCollectionPage(
    query: EstimateTemplatesCollectionQuery,
    forceRefresh = false,
  ): Observable<PagedRestResponse<EstimateTemplateModel[]>> {
    return this.getEstimateTemplatesCollectionResponse(query, forceRefresh)
  }

  getEstimateCategories(
    query: EstimateCategoriesCollectionQuery = {},
    forceRefresh = false,
  ): Observable<EstimateCategoryModel[]> {
    const route = '/estimates/categories'
    const params = this.convertToParams(query)
    const headers = forceRefresh
      ? new HttpHeaders({ 'x-refresh': 'true' })
      : undefined

    return this.get<EstimateCategoryModel[]>(route, params, headers ? { headers } : undefined).pipe(
      oMap((response) => lodashMap(response.data, (category) => new EstimateCategoryModel(category))),
    )
  }

  getEstimateCategoriesPage(
    query: EstimateCategoriesCollectionQuery = {},
    forceRefresh = false,
  ): Observable<PagedRestResponse<EstimateCategoryModel[]>> {
    const route = '/estimates/categories'
    const params = this.convertToParams(query)
    const headers = forceRefresh
      ? new HttpHeaders({ 'x-refresh': 'true' })
      : undefined

    return this.get<EstimateCategoryModel[]>(route, params, headers ? { headers } : undefined).pipe(
      oMap((response) => {
        return {
          ...response,
          data: lodashMap(response.data, (category) => new EstimateCategoryModel(category)),
        }
      }),
    )
  }

  createEstimateCategory(input: {
    name: string
    parentId?: string | null
    status: EstimateCategoryStatus
  }): Observable<RestResponse<EstimateCategoryModel>> {
    return this.post<EstimateCategoryModel>('/crud/estimate-categories', input)
  }

  updateEstimateCategory(
    id: string,
    input: {
      name: string
      parentId?: string | null
      status: EstimateCategoryStatus
    },
  ): Observable<RestResponse<EstimateCategoryModel>> {
    return this.put<EstimateCategoryModel>(`/crud/estimate-categories/${id}`, input)
  }

  deleteEstimateCategory(id: string): Observable<RestResponse<EstimateCategoryModel>> {
    return this.delete<EstimateCategoryModel>(`/crud/estimate-categories/${id}`)
  }

  createEstimateTemplate(input: {
    name: string
    facilityIds: string[]
    categoryId?: string | null
    charges: any[]
  }): Observable<RestResponse<EstimateTemplateModel>> {
    return this.post<EstimateTemplateModel>('/crud/estimate-templates', input)
  }

  updateEstimateTemplate(
    id: string,
    input: {
      name: string
      facilityIds: string[]
      categoryId?: string | null
      charges: any[]
    },
  ): Observable<RestResponse<EstimateTemplateModel>> {
    return this.put<EstimateTemplateModel>(`/crud/estimate-templates/${id}`, input)
  }

  deleteEstimateTemplate(id: string): Observable<RestResponse<EstimateTemplateModel>> {
    return this.delete<EstimateTemplateModel>(`/crud/estimate-templates/${id}`)
  }

  private getEstimateTemplatesCollectionResponse(
    query: EstimateTemplatesCollectionQuery,
    forceRefresh = false,
  ): Observable<PagedRestResponse<EstimateTemplateModel[]>> {
    const route = '/estimates/templates'
    const params = this.convertToParams(query)
    const headers = forceRefresh
      ? new HttpHeaders({ 'x-refresh': 'true' })
      : undefined

    return this.get<EstimateTemplateModel[]>(route, params, headers ? { headers } : undefined).pipe(
      oMap((response) => {
        return {
          ...response,
          data: lodashMap(response.data, (template) => new EstimateTemplateModel(template)),
        }
      }),
    )
  }
}